import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ShieldAlert, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Card, Label, Input, Button } from '../components/ui';

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [novo, setNovo] = useState({ nome: '', email: '', senha: '', role: 'USER' });
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  useEffect(() => { carregar(); }, []);

  const carregar = () => {
    api.get('/usuarios').then(res => setUsuarios(res.data)).catch(err => console.error(err));
  };

  const adicionar = async () => {
    if (!novo.nome || !novo.email || !novo.senha) return toast.error("Preencha todos os campos!");
    if (novo.senha.length < 6) return toast.error("A senha deve ter no mínimo 6 caracteres.");

    try {
      await api.post('/usuarios', novo);
      toast.success("Usuário criado com sucesso!");
      setNovo({ nome: '', email: '', senha: '', role: 'USER' }); 
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar usuário.");
    }
  };

  const deletar = async (id, role) => {
    if (role === 'ADMIN' && usuarios.filter(u => u.role === 'ADMIN').length === 1) {
      return toast.error("Você não pode excluir o último administrador do sistema!");
    }
    
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-slate-900">Excluir este acesso permanentemente?</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="h-8 px-3 text-xs" onClick={() => toast.dismiss(t.id)}>Cancelar</Button>
          <Button variant="destructive" className="h-8 px-3 text-xs" onClick={async () => {
            toast.dismiss(t.id);
            try {
              await api.delete(`/usuarios/${id}`); 
              toast.success("Acesso removido.");
              carregar();
            } catch (e) { toast.error("Erro ao deletar."); }
          }}>Confirmar</Button>
        </div>
      </div>
    ), { duration: 5000 });
  };

  return (
    <div className="space-y-6 animate-in fade-in pb-20">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Gestão de Usuários</h2>
        <p className="text-slate-500 text-sm">Crie e gerencie os acessos ao sistema.</p>
      </div>

      <Card className="p-4 sm:p-6 border-t-4 border-t-brand">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-slate-800">
          <ShieldAlert size={20} className="text-brand" /> Adicionar Novo Acesso
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <Label>Nome</Label>
            <Input placeholder="Nome completo" value={novo.nome} onChange={e => setNovo({...novo, nome: e.target.value})} />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" placeholder="email@empresa.com" value={novo.email} onChange={e => setNovo({...novo, email: e.target.value})} />
          </div>
          <div>
            <Label>Senha</Label>
            <Input type="password" placeholder="••••••" value={novo.senha} onChange={e => setNovo({...novo, senha: e.target.value})} />
          </div>

          <div className="relative">
            <Label>Nível de Acesso</Label>
            <div 
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 cursor-pointer items-center justify-between focus:ring-2 focus:ring-slate-400"
              onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            >
              <span className={novo.role === 'ADMIN' ? 'font-bold text-brand' : ''}>
                {novo.role === 'ADMIN' ? 'Administrador Total' : 'Usuário Comum'}
              </span>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
            </div>
            
            {showRoleDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden animate-in zoom-in-95 duration-100">
                <div 
                  className="px-3 py-2.5 cursor-pointer hover:bg-slate-100 text-sm text-slate-700 flex flex-col"
                  onClick={() => { setNovo({...novo, role: 'USER'}); setShowRoleDropdown(false); }}
                >
                  <span className="font-bold">Usuário Comum</span>
                  <span className="text-[10px] text-slate-500 uppercase">Apenas operação diária</span>
                </div>
                <div 
                  className="px-3 py-2.5 cursor-pointer hover:bg-slate-100 text-sm text-slate-700 border-t border-slate-50 flex flex-col"
                  onClick={() => { setNovo({...novo, role: 'ADMIN'}); setShowRoleDropdown(false); }}
                >
                  <span className="font-bold text-brand">Administrador Total</span>
                  <span className="text-[10px] text-slate-500 uppercase">Controle total do sistema</span>
                </div>
              </div>
            )}
            {showRoleDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowRoleDropdown(false)} />}
          </div>

          <Button onClick={adicionar} className="w-full bg-slate-900 hover:bg-slate-800 h-10">
            <Plus size={18} className="mr-2"/> Salvar Acesso
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-sm text-left min-w-[700px]">
            <thead className="bg-slate-50 border-b text-slate-700">
              <tr>
                <th className="px-6 py-4 font-medium uppercase text-[10px] tracking-wider">Nome</th>
                <th className="px-6 py-4 font-medium uppercase text-[10px] tracking-wider">E-mail (Login)</th>
                <th className="px-6 py-4 font-medium uppercase text-[10px] tracking-wider">Nível</th>
                <th className="px-6 py-4 font-medium text-right uppercase text-[10px] tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usuarios.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-slate-900">{u.nome}</td>
                  <td className="px-6 py-4 text-slate-600">{u.email}</td>
                  <td className="px-6 py-4">
                    {u.role === 'ADMIN' 
                      ? <span className="bg-orange-50 text-brand px-2.5 py-1 rounded-full text-[10px] font-bold border border-brand/20">ADMINISTRADOR</span>
                      : <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-[10px] font-bold border border-slate-200">USUÁRIO COMUM</span>
                    }
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button 
                      variant="destructive" 
                      className="h-8 w-8 !p-0"
                      onClick={() => deletar(u.id, u.role)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}